import { useState, useEffect } from "react";
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  Select,
  VStack,
  FormErrorMessage,
} from "@chakra-ui/react";
import TipTapEditor from "../common/TipTapEditor";

const DEFAULT_REFLECTION_TITLE = "Reflection";

const WidgetForm = ({ initialData = null, onSubmit }) => {
  const [title, setTitle] = useState("");
  const [overview, setOverview] = useState("");
  const [reflection, setReflection] = useState("");
  const [reflectionTitle, setReflectionTitle] = useState(DEFAULT_REFLECTION_TITLE);
  const [language, setLanguage] = useState("en");
  const [errors, setErrors] = useState({});

  // ✅ Initialize fields only when initialData changes
  useEffect(() => {
    if (initialData) {
      setTitle(initialData.title || "");
      setOverview(initialData.overview || "");
      setReflection(initialData.reflection || "");
      setReflectionTitle(
        initialData.reflection_title || DEFAULT_REFLECTION_TITLE
      );
      setLanguage(initialData.language || "en");
    }
  }, [initialData]);

  const validate = () => {
    const newErrors = {};

    if (!title.trim()) {
      newErrors.title = "Title is required";
    }

    if (!overview || overview.replace(/<[^>]+>/g, "").trim() === "") {
      newErrors.overview = "Overview is required";
    }

    if (!reflection || reflection.replace(/<[^>]+>/g, "").trim() === "") {
      newErrors.reflection = "Content is required";
    }

    // Optional — keep loose unless product says otherwise
    if (!reflectionTitle.trim()) {
      newErrors.reflectionTitle = "Section title is required";
    }

    return newErrors;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    onSubmit({
      title,
      overview,
      reflection,
      reflection_title: reflectionTitle,
      language,
    });
  };

  return (
    <Box as="form" onSubmit={handleSubmit} p={4}>
      <VStack spacing={6} align="stretch">
        {/* Title */}
        <FormControl isInvalid={errors.title}>
          <FormLabel>Title</FormLabel>
          <Input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Enter widget title"
          />
          <FormErrorMessage>{errors.title}</FormErrorMessage>
        </FormControl>

        {/* Overview */}
        <FormControl isInvalid={errors.overview}>
          <FormLabel>Overview</FormLabel>
          <Box border="1px solid" borderColor="gray.200" borderRadius="md" p={2}>
            <TipTapEditor
              key="overview-editor"
              content={overview}
              onChange={setOverview}
            />
          </Box>
          <FormErrorMessage>{errors.overview}</FormErrorMessage>
        </FormControl>

        {/* Reflection Title */}
        <FormControl isInvalid={errors.reflectionTitle}>
          <FormLabel>Reflection section title</FormLabel>
          <Input
            value={reflectionTitle}
            onChange={(e) => setReflectionTitle(e.target.value)}
            placeholder="e.g. Reflection, Next Steps, Key Insight"
          />
          <FormErrorMessage>{errors.reflectionTitle}</FormErrorMessage>
        </FormControl>

        {/* Reflection Content */}
        <FormControl isInvalid={errors.reflection}>
          <FormLabel>{reflectionTitle || DEFAULT_REFLECTION_TITLE}</FormLabel>
          <Box border="1px solid" borderColor="gray.200" borderRadius="md" p={2}>
            <TipTapEditor
              key="reflection-editor"
              content={reflection}
              onChange={setReflection}
            />
          </Box>
          <FormErrorMessage>{errors.reflection}</FormErrorMessage>
        </FormControl>

        {/* Language */}
        <FormControl>
          <FormLabel>Language</FormLabel>
          <Select
            value={language}
            onChange={(e) => setLanguage(e.target.value)}
          >
            <option value="en">English</option>
            <option value="sv">Swedish</option>
          </Select>
        </FormControl>

        {/* Submit */}
        <Button type="submit" colorScheme="orange" alignSelf="flex-start">
          Save Widget
        </Button>
      </VStack>
    </Box>
  );
};

export default WidgetForm;